/**
 * Football Player Designation Rules
 * 
 * Defines characteristic traits (designations) that Football players can earn
 * based on their attributes and positions (e.g., "Game Wrecker", "Field General")
 */

import type { DesignationRule, PositionCategories } from '../../utils/playerDesignations/types';

/**
 * Football Position Categories
 */
export const FOOTBALL_POSITION_CATEGORIES: PositionCategories = {
  offensive: ['QB', 'RB', 'WR', 'TE', 'OL'],
  defensive: ['DL', 'LB', 'DB'],
};

/**
 * Football Designation Rules
 * Each designation requires at least 3 matching attributes and must have position requirements
 */
export const FOOTBALL_DESIGNATIONS: DesignationRule[] = [
  // DEFENSIVE DESIGNATIONS
  {
    name: 'Game Wrecker',
    description: 'Dominant defensive player with elite tackling, strength, awareness, and toughness',
    conditions: {
      attributes: {
        tackling: { min: 9 },
        strength: { min: 8 },
        awareness: { min: 9 },
        toughness: { min: 9 },
        stamina: { min: 9 },
        agility: { min: 7 },
      },
      positions: ['DL', 'LB'],
    },
    priority: 10,
    side: 'defense',
  },
  {
    name: 'Heavy Hitter',
    description: 'Physical defensive player with high toughness, strength, tackling, and stamina',
    conditions: {
      attributes: {
        toughness: { min: 8 },
        strength: { min: 8 },
        tackling: { min: 7 },
        stamina: { min: 7 },
        speed: { min: 6 },
        awareness: { min: 6 },
      },
      positions: ['DL', 'LB'],
    },
    priority: 9,
    side: 'defense',
  },
  {
    name: 'Sack Machine',
    description: 'Elite tackling and speed with strength, agility, and awareness',
    conditions: {
      attributes: {
        tackling: { min: 9 },
        speed: { min: 8 },
        strength: { min: 7 },
        agility: { min: 8 },
        awareness: { min: 8 },
        toughness: { min: 8 },
      },
      positions: ['DL', 'LB'],
    },
    priority: 6,
    side: 'defense',
  },
  {
    name: 'Lockdown',
    description: 'Elite awareness and agility with speed, catching, and focus',
    conditions: {
      attributes: {
        awareness: { min: 8 },
        agility: { min: 8 },
        speed: { min: 8 },
        catching: { min: 7 },
        focus: { min: 8 },
        sport_iq: { min: 8 },
      },
      positions: ['DB'],
    },
    priority: 6,
    side: 'defense',
  },
  {
    name: 'Ball Hawk',
    description: 'Elite awareness, catching, and agility with route running',
    conditions: {
      attributes: {
        awareness: { min: 8 },
        catching: { min: 8 },
        agility: { min: 7 },
        route_running: { min: 7 },
        ball_security: { min: 6 },
        focus: { min: 8 },
      },
      positions: ['DB'],
    },
    priority: 6,
    side: 'defense',
  },

  // OFFENSIVE DESIGNATIONS
  {
    name: 'Field General',
    description: 'Elite QB with high awareness, coachability, sport IQ, and throwing ability',
    conditions: {
      attributes: {
        awareness: { min: 8 },
        coachability: { min: 9 },
        sport_iq: { min: 9 },
        throwing_power: { min: 8 },
        throwing_accuracy: { min: 8 },
        focus: { min: 8 },
        attitude: { min: 8 },
      },
      positions: ['QB'],
    },
    priority: 7,
    side: 'offense',
  },
  {
    name: 'Rocket Arm',
    description: 'Elite throwing power and accuracy with awareness and sport IQ',
    conditions: {
      attributes: {
        throwing_power: { min: 9 },
        throwing_accuracy: { min: 9 },
        awareness: { min: 8 },
        sport_iq: { min: 7 },
        strength: { min: 6 },
        focus: { min: 8 },
      },
      positions: ['QB'],
    },
    priority: 7,
    side: 'offense',
  },
  {
    name: 'Speed Demon',
    description: 'Elite speed with agility, stamina, and route running',
    conditions: {
      attributes: {
        speed: { min: 9 },
        agility: { min: 7 },
        stamina: { min: 7 },
        route_running: { min: 6 },
        ball_security: { min: 6 },
      },
      positions: ['RB', 'WR', 'TE'],
    },
    priority: 8,
    side: 'offense',
  },
  {
    name: 'Mr. Elusive',
    description: 'Elite agility with speed, route running, and ball security',
    conditions: {
      attributes: {
        agility: { min: 9 },
        speed: { min: 7 },
        route_running: { min: 7 },
        ball_security: { min: 7 },
        awareness: { min: 8 },
      },
      positions: ['RB', 'WR', 'TE'],
    },
    priority: 8,
    side: 'offense',
  },
  {
    name: 'Route Technician',
    description: 'Elite route running with catching, awareness, and agility',
    conditions: {
      attributes: {
        route_running: { min: 8 },
        catching: { min: 8 },
        awareness: { min: 7 },
        agility: { min: 8 },
        speed: { min: 7 },
        ball_security: { min: 6 },
      },
      positions: ['WR', 'TE', 'RB'],
    },
    priority: 6,
    side: 'offense',
  },
  {
    name: 'Power Runner',
    description: 'High strength and toughness with speed, agility, and ball security',
    conditions: {
      attributes: {
        strength: { min: 8 },
        toughness: { min: 8 },
        speed: { min: 7 },
        agility: { min: 7 },
        ball_security: { min: 8 },
        stamina: { min: 8 },
      },
      positions: ['RB', 'WR', 'TE'],
    },
    priority: 5,
    side: 'offense',
  },
  {
    name: 'Protector',
    description: 'Elite blocking with strength, toughness, and awareness',
    conditions: {
      attributes: {
        blocking: { min: 8 },
        strength: { min: 8 },
        toughness: { min: 8 },
        awareness: { min: 7 },
        stamina: { min: 8 },
        coachability: { min: 8 },
      },
      positions: ['OL', 'TE'],
    },
    priority: 7,
    side: 'offense',
  },
  {
    name: 'Snap Master',
    description: 'Elite snapping with awareness, strength, and coachability',
    conditions: {
      attributes: {
        snapping: { min: 8 },
        awareness: { min: 7 },
        strength: { min: 8 },
        coachability: { min: 8 },
        focus: { min: 8 },
        blocking: { min: 8 },
      },
      positions: ['OL'],
    },
    priority: 6,
    side: 'offense',
  },

  // UNIVERSAL DESIGNATIONS (can apply to both sides, but still need position requirements)
  {
    name: 'Iron Man',
    description: 'Elite stamina and toughness with strength and coachability',
    conditions: {
      attributes: {
        stamina: { min: 9 },
        toughness: { min: 8 },
        strength: { min: 8 },
        coachability: { min: 7 },
        focus: { min: 7 },
        attitude: { min: 8 },
      },
      positions: ['QB', 'RB', 'WR', 'TE', 'OL', 'DL', 'LB', 'DB'],
    },
    priority: 5,
    side: 'offense', // Default to offense, but can match either side
  },
  {
    name: 'Clutch Performer',
    description: 'High focus, sport IQ, awareness, and coachability',
    conditions: {
      attributes: {
        focus: { min: 8 },
        sport_iq: { min: 8 },
        awareness: { min: 8 },
        coachability: { min: 8 },
        attitude: { min: 8 },
        ball_security: { min: 7 },
      },
      positions: ['QB', 'RB', 'WR', 'TE', 'OL', 'DL', 'LB', 'DB'],
    },
    priority: 5,
    side: 'offense', // Default to offense, but can match either side
  },
];

